import React, { memo, useCallback } from 'react';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import PickersDay, { PickersDayProps } from '@mui/lab/PickersDay';
import { Box, FormControl, TextField, TextFieldProps, Typography } from '@mui/material';
import { commonKeyboardDatePickerProps } from '@provider-portal/common/components/StartEndDatePicker/const';
import { Day } from '@provider-portal/common/components/StartEndDatePicker/Day';
import type { Nullable } from '@provider-portal/common/types/types';
import { format } from 'date-fns';
import { DateTime } from 'luxon';

import { getEndOfDay } from 'utils/date';

interface PickerProps {
  minDate?: any;
  maxDate?: any;
}

export type StartEndDatePickerProps = React.PropsWithChildren<{
  strings: {
    start: string;
    startDescription?: string;
    end: string;
    endDescription?: string;
    minEndDateMessageError?: string;
  };
  startDate: Nullable<number>;
  endDate: Nullable<number>;
  onChange: (startMS: Nullable<number>, endMS: Nullable<number>) => void;
  startPickerProps?: PickerProps;
  endPickerProps?: PickerProps;
}>;

export const StartEndDatePicker = memo((props: StartEndDatePickerProps) => {
  const { strings, startDate, endDate, onChange, startPickerProps, endPickerProps, children } =
    props;

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      onChange(date && date.getTime(), endDate);
    },
    [endDate, onChange],
  );

  const renderInput = useCallback(
    (params: TextFieldProps) => <TextField {...params} fullWidth variant="filled" />,
    [],
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      onChange(startDate, date ? getEndOfDay(DateTime.fromJSDate(date)).getTime() : null);
    },
    [onChange, startDate],
  );

  const renderEndDay = useCallback(
    (date: Date, _selectedDates: Array<Date | null>, pickersDayProps: PickersDayProps<Date>) => {
      if (startDate && date?.getTime() === startDate) {
        return (
          <Day {...pickersDayProps} highlighted>
            {format(date, 'd')}
          </Day>
        );
      }
      return <PickersDay {...pickersDayProps} />;
    },
    [startDate],
  );

  return (
    <Box display="flex" alignItems="flex-end" flexWrap="wrap" gap={2}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box flexGrow={1} minWidth={{ xs: '100%', md: '33%' }}>
          <FormControl fullWidth>
            <Box mb={1}>
              <Typography variant="subtitleS">{strings.startDescription}</Typography>
            </Box>
            <DesktopDatePicker
              {...commonKeyboardDatePickerProps}
              {...startPickerProps}
              label={strings.start}
              value={startDate}
              onChange={handleStartDateChange}
              renderInput={renderInput}
            />
          </FormControl>
        </Box>
        <Box flexGrow={1} minWidth={{ xs: '100%', md: '33%' }}>
          <FormControl fullWidth>
            <Box mb={1}>
              <Typography variant="subtitleS">{strings.endDescription}</Typography>
            </Box>
            <DesktopDatePicker
              {...commonKeyboardDatePickerProps}
              {...endPickerProps}
              label={strings.end}
              value={endDate}
              onChange={handleEndDateChange}
              renderInput={renderInput}
              renderDay={renderEndDay}
            />
          </FormControl>
        </Box>
        {children}
      </LocalizationProvider>
    </Box>
  );
});
